<template>
  <z-form ref="form" @submit.native.prevent="save">
    <div class="reset-password-success my-3">
      {{ $t('core.profile.block.change_password.title') }}
    </div>

    <z-form-input
      class="mb-5"
      :rules="{
        required: true,
        min: 6,
        max: 128,
        confirmed: '@new_password_confirmation',
        password: true
      }"
      vid="new_password"
    >
      <div class="flex" style="align-items: baseline">
        <label class="mr-2">
          {{ $t('core.profile.form.field.password.new') }}
        </label>
      </div>

      <i-input v-model="form.new_password" :type="password_params.type" class="input--eye-icon">
        <template #suffix>
          <icon
            v-if="!!form.new_password"
            :type="password_params.icon"
            size="20"
            @click="toggle_password"
          />
        </template>
      </i-input>
      <popup width="200px" style="top: -35px">
        <p>{{ $t('core.reset_password.password_requirements') }}</p>
        <conditions :valid_result="valid_result" />
        <div
          :class="{ link: true, 'mt-2': true, 'link--disabled': is_generation_loading }"
          @click="generate_password"
        >
          {{ $t('core.reset_password.generate_password') }}
        </div>
      </popup>
    </z-form-input>

    <z-form-input
      class="mb-5"
      :rules="{ required: true, min: 6, max: 128 }"
      vid="new_password_confirmation"
    >
      <label>
        {{ $t('core.profile.form.field.password.new_confirm') }}
      </label>
      <i-input v-model="form.new_password_confirmation" :type="confirm_password_params.type" class="input--eye-icon">
        <template #suffix>
          <icon
            v-if="!!form.new_password_confirmation"
            :type="confirm_password_params.icon"
            size="20"
            @click="toggle_confirm_password"
          />
        </template>
      </i-input>
    </z-form-input>

    <div class="mt-4 text-right">
      <i-button
        html-type="submit"
        class="auth-content__form__submit"
        type="primary"
        :loading="is_loading"
        :disabled="!password_is_valid || is_loading"
      >
        {{ $t('shared.component.button.change') }}
      </i-button>
    </div>
  </z-form>
</template>

<script>
  import { ResetPasswordService } from '@core/services/ResetPasswordService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import Conditions from './conditions';
  import { validations } from '@utils/password-rules';

  export default {
    name: 'ResetPasswordForm',
    components: { Conditions },

    mixins: [PageServerValidationResponseMixin],
    props: {
      email: {
        type: String,
        default: ''
      },
      code: {
        type: String,
        default: ''
      }
    },
    data: () => ({
      form: {
        new_password: '',
        new_password_confirmation: ''
      },
      password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      confirm_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      valid_result: [null, null, null, null, null],
      is_loading: false,
      is_generation_loading: false
    }),

    computed: {
      password_is_valid: function () {
        return Object.entries(this.valid_result).every(([key, results]) => !!results);
      }
    },
    watch: {
      'form.new_password': {
        handler: function (value) {
          if (this.form.new_password == '') {
            this.password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(value, this.form.new_password_confirmation)
                : fn(value, this.form.new_password_confirmation) || null;
            } else {
              return this.valid_result[i] === false ? fn(value) : fn(value) || null;
            }
          });
        },
        immediate: true,
        deep: true
      },
      'form.new_password_confirmation': {
        handler: function (value) {
          if (this.form.new_password_confirmation == '') {
            this.confirm_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(this.form.new_password, value)
                : fn(this.form.new_password, value) || null;
            } else {
              return this.valid_result[i] === false
                ? fn(this.form.new_password)
                : fn(this.form.new_password) || null;
            }
          });
        },
        immediate: true,
        deep: true
      }
    },
    methods: {
      toggle_password() {
        this.password_params = {
          type: this.password_params.type === 'password' ? 'text' : 'password',
          icon: this.password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },
      toggle_confirm_password() {
        this.confirm_password_params = {
          type: this.confirm_password_params.type === 'password' ? 'text' : 'password',
          icon: this.confirm_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },
      generate_password: function () {
        this.is_generation_loading = true;
        ResetPasswordService.generate()
          .then(({ password }) => {
            this.form.new_password = password || '';
            this.password_params = {
              type: (this.password_params.type = 'text'),
              icon: (this.password_params.icon = 'md-eye-off')
            };
          })
          .finally(() => (this.is_generation_loading = false));
      },
      save: function () {
        if (this.password_is_valid) {
          this.is_loading = true;
          ResetPasswordService.changePassword({
            email: this.email,
            email_code: this.code,
            ...this.form
          })
            .then(() => {
              setTimeout(() => {
                this.$router.push({ name: 'auth.login' });
              }, 2000);
              this.$noty.success(
                this.$t('core.reset_password.success_change_password') +
                  ' ' +
                  this.$t('core.reset_password.link.success_change_link_to_auth_page')
              );
              this.is_loading = false;
            })
            .catch((err) => this.check_server_validation(err))
            .finally(() => (this.is_loading = false));
        }
      }
    }
  };
</script>
<style >
  .input--eye-icon .ivu-input-suffix {
    top: 2px;
  }
</style>
